// Hook that maintains a WebSocket to /api/ws/ringing for the current
// (active) domain and surfaces inbound ringing-popup events as state.
//
// Auth: we pass the JWT via ?token=… in the connect URL because the
// browser WebSocket API can't easily set custom headers on the upgrade.
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

const wsBase = () => {
  const http = process.env.REACT_APP_BACKEND_URL || '';
  return http.replace(/^http/, 'ws');
};

export const useRingingSocket = (domainId) => {
  const { user, token } = useAuth();
  const [event, setEvent] = useState(null);
  const [callState, setCallState] = useState(null); // {type:'call_answered_playbook', ...} | {type:'call_ended'} | null
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);

  useEffect(() => {
    if (!domainId || !token) return undefined;
    if (user?.role !== 'master_admin' && user?.domain_id !== domainId) return undefined;
    let cancelled = false;

    const connect = () => {
      const url = `${wsBase()}/api/ws/ringing?domain_id=${encodeURIComponent(domainId)}&token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data?.type === 'ringing_popup') setEvent({ ...data, _id: Date.now() });
          else if (data?.type === 'call_answered_playbook') setCallState({ ...data, _id: Date.now() });
          else if (data?.type === 'playbook_hint_corrected') setCallState({ ...data, _id: Date.now() });
          else if (data?.type === 'call_ended') setCallState({ type: 'call_ended', _id: Date.now() });
        } catch (err) {
          // Malformed WS payload — log but don't crash the socket
          console.warn('[useRingingSocket] bad message payload', err);
        }
      };
      ws.onclose = () => {
        if (cancelled) return;
        // Backoff reconnect (3-5s) so we don't hammer a flaky server
        const delay = 3000 + Math.random() * 2000;
        reconnectRef.current = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        try { ws.close(); }
        catch (err) { console.warn('[useRingingSocket] close after error failed', err); }
      };
    };

    connect();
    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      try { wsRef.current?.close(); }
      catch (err) { console.warn('[useRingingSocket] close on unmount failed', err); }
    };
  }, [domainId, token, user?.role, user?.domain_id]);

  return { event, callState };
};
